import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import ResponsiveDrawer from '../../Admin/Components/Drawer/Drawer'; 
import { useNavigate } from 'react-router-dom';
import { getAllAds, deleteAdById } from '../../Services/AdminServices';

const AdsPage = () => {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [currentAd, setCurrentAd] = useState({
    id: null,
    photo: '',
    createdAt: ''
  });
  const [apiResponseDebug, setApiResponseDebug] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    adId: null
  });

  // Fetch all ads when component mounts
  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const response = await getAllAds();
      
      // Store the raw response for debugging
      setApiResponseDebug(response);
      
      console.log("API Response:", response);
      
      // Try different possible response formats
      let adsData = [];
      
      // Case 1: response is an array directly
      if (Array.isArray(response)) {
        adsData = response;
      } 
      // Case 2: response has an ads property that's an array
      else if (response && Array.isArray(response.ads)) {
        adsData = response.ads;
      } 
      // Case 3: response is an object with data property
      else if (response && Array.isArray(response.data)) {
        adsData = response.data;
      }
      // Case 4: response has a data.ads property
      else if (response && response.data && Array.isArray(response.data.ads)) {
        adsData = response.data.ads;
      }
      // If we still don't have valid data, show error
      
      if (adsData.length > 0) {
        console.log("Processed ads data:", adsData);
        setAds(adsData);
        setError(null);
      } else {
        setAds([]);
        setError("Could not find ads data in API response");
      }
    } catch (err) {
      console.error("Error fetching ads:", err);
      setAds([]);
      setError("Failed to load ads. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Create preview URL
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPreviewUrl(fileReader.result);
      };
      fileReader.readAsDataURL(file);
    }
  };

  const handleOpenDialog = (ad = null) => {
    if (ad) {
      setCurrentAd({
        id: ad._id || ad.id, // Handle both possible property names
        photo: ad.image?.url || ad.photo || '', // Handle both possible structures
        createdAt: ad.createdAt
      });
    } else {
      setCurrentAd({ id: null, photo: '', createdAt: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleClick = () => {
    navigate('/admin/Newads');  // Navigate to the page for adding new ads
  };

  const handleSaveAd = () => {
    // This function would be updated to use API endpoints for creating/updating ads
    handleCloseDialog();
  };

  // Show confirmation dialog before delete
  const confirmDelete = (adId) => {
    setDeleteConfirmDialog({
      open: true,
      adId
    });
  };

  const handleCloseConfirmDialog = () => {
    setDeleteConfirmDialog({
      open: false,
      adId: null
    });
  };

  const handleDeleteAd = async (adId) => {
    if (!adId) {
      console.error("Cannot delete ad: ID is missing");
      setSnackbar({
        open: true,
        message: "Failed to delete ad: No ID provided",
        severity: "error"
      });
      return;
    }
  
    try {
      setDeleteLoading(true);
      console.log("Deleting ad with ID:", adId);
  
      const response = await deleteAdById(adId);
      console.log("Delete response:", response);
  
      // Refresh ads after delete
      await fetchAds();
  
      setSnackbar({
        open: true,
        message: "Ad deleted successfully!",
        severity: "success"
      });
  
      handleCloseConfirmDialog();
    } catch (err) {
      console.error("Error deleting ad:", err);
      setSnackbar({
        open: true,
        message: `Failed to delete ad: ${err.message || 'Unknown error'}`,
        severity: "error"
      });
    } finally {
      setDeleteLoading(false);
    }
  };
  

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        // hour: '2-digit',
        // minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Function to get image URL from multiple possible data structures
  const getImageUrl = (ad) => {
    if (ad.image?.url) return ad.image.url;
    if (ad.photo) return ad.photo;
    if (ad.url) return ad.url;
    return null;
  };

  // Safe accessor for ID
  const getId = (ad) => {
    return ad._id || ad.id;
  };

  // Make sure ads is an array before filtering
  const filteredAds = Array.isArray(ads) 
    ? ads.filter(ad => {
        const imageUrl = getImageUrl(ad) || '';
        const createdAtFormatted = formatDate(ad.createdAt);
        return imageUrl.toLowerCase().includes(searchTerm.toLowerCase()) ||
               createdAtFormatted.toLowerCase().includes(searchTerm.toLowerCase());
      })
    : [];

  return (
    <Container maxWidth="lg" sx={{ padding: 0 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={2}>
          <ResponsiveDrawer />
        </Grid>
        <Grid item xs={12} lg={10} sx={{ mt: 6 }}>
          <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Container maxWidth="lg">
            <Box sx={{ my: 4 }}>
  <Grid container spacing={2} alignItems="center">
    {/* Heading */}
    <Grid item xs="auto">
      <Grid item xs sx={{ pr: 50 }}>
                     <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', marginLeft: 3 }}>
                       Ads
                     </Typography>
                   </Grid>
    </Grid>

    {/* Search Field */}
    <Grid item xs>
      <TextField
        placeholder="Search..."
        size="small"
        fullWidth
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ backgroundColor: '#F4F4F4', marginRight: 1, marginLeft: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
    </Grid>

    {/* Create Button */}
    <Grid
  item
  xs={12}
  sm="auto"
  sx={{
    display: 'flex',
    justifyContent: { sm: 'flex-end' },
    mt: { xs: 1, sm: 0 },
    ml: { xs: 2, sm: 0 }, // 👈 add margin-left in mobile
  }}
>
  <Button
    variant="contained"
    startIcon={<AddIcon />}
    onClick={handleClick}
    sx={{
      backgroundColor: '#a06373',
      '&:hover': { backgroundColor: '#865561' },
      px: 3,
      width: 'auto',
    }}
  >
    CREATE
  </Button>
</Grid>

  </Grid>
</Box>





              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress sx={{ color: '#a06373' }} />
                </Box>
              ) : error ? (
                <>
                  <Paper sx={{ p: 3, textAlign: 'center', color: 'error.main', mb: 2 }}>
                    {error}
                  </Paper>
                  {apiResponseDebug && (
                    <Paper sx={{ p: 3, mb: 2 }}>
                      <Typography variant="h6">API Response Debug</Typography>
                      <pre style={{ overflow: 'auto', maxHeight: '200px' }}>
                        {JSON.stringify(apiResponseDebug, null, 2)}
                      </pre>
                    </Paper>
                  )}
                </>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#A56266' }}>
                        <TableCell sx={{ backgroundColor: "#caada7", color: "white", fontWeight: "bold" }}>Action</TableCell>
                        <TableCell sx={{ backgroundColor: "#d18898", color: "white", fontWeight: "bold" }}>Photo</TableCell>
                        <TableCell sx={{ backgroundColor: "#e9a9a9", color: "white", fontWeight: "bold" }}>Created At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAds.length > 0 ? (
                        filteredAds.map((ad, index) => (
                          <TableRow 
                            key={getId(ad) || index}
                            sx={{ backgroundColor: index % 2 === 0 ? '#f3f4d3' : '#e0dab8'}}
                          >
                            <TableCell>
                              <IconButton 
                                size="small" 
                                onClick={() => confirmDelete(getId(ad))}
                                disabled={deleteLoading}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              {getImageUrl(ad) && (
                                <img 
                                  src={getImageUrl(ad)}
                                  alt={`Ad ${getId(ad)}`}
                                  style={{ height: 40, width: 'auto' }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "/placeholder-image.jpg";
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {formatDate(ad.createdAt)}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            No ads found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Container>
          </Box>

          {/* Ad Dialog */}
          <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              {currentAd.id ? "Edit Ad" : "Add New Ad"}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ border: '1px dashed #ccc', p: 3, borderRadius: 1, mt: 2, textAlign: 'center' }}>
                <input
                  type="file"
                  id="ad-photo-upload"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="ad-photo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                  >
                    Upload Photo
                  </Button>
                </label>
                
                {previewUrl ? (
                  <Box sx={{ mt: 2 }}>
                    <img 
                      src={previewUrl} 
                      alt="Preview" 
                      style={{ maxWidth: '100%', maxHeight: 200 }}
                    />
                    <Typography variant="caption" display="block">
                      {selectedFile?.name}
                    </Typography>
                  </Box>
                ) : currentAd.photo ? (
                  <Box sx={{ mt: 2 }}>
                    <img 
                      src={currentAd.photo}
                      alt={`Ad ${currentAd.id}`}
                      style={{ maxWidth: '100%', maxHeight: 200 }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/placeholder-image.jpg"; // Fallback image
                      }}
                    />
                    <Typography variant="caption" display="block">
                      Created: {formatDate(currentAd.createdAt)}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    No image selected
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} color="primary">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveAd} 
                variant="contained" 
                color="primary"
                sx={{ backgroundColor: '#a06373', '&:hover': { backgroundColor: '#865561' } }}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteConfirmDialog.open} onClose={handleCloseConfirmDialog}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Typography>Are you sure you want to delete this ad?</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseConfirmDialog} color="primary">
                Cancel
              </Button>
              <Button 
                onClick={() => handleDeleteAd(deleteConfirmDialog.adId)} 
                color="error" 
                variant="contained"
                disabled={deleteLoading}
              >
                {deleteLoading ? <CircularProgress size={24} /> : "Delete"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={handleCloseSnackbar} 
              severity={snackbar.severity} 
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AdsPage;